import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import * as Sentry from '@sentry/nextjs';
import config from 'config';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import { useCustomer, useCustomerDetails } from '@techstyle/react-accounts';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
} from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import { loadAddresses, setDefaultAddress } from '../../actions/account';
import {
  braintreePaypalCheckout,
  getShippingOptions,
  setBraintreeDeviceData,
  trackExpressCheckoutSelection,
  updateCartWithShipping,
} from '../../actions/checkout';
import { useLDNewShoppingBagUX } from '../../utils/LD/useNewShoppingBagUX';
import {
  filterShippingOptionsByAddress,
  getCountryList,
  getShippingInfo,
} from '../../utils/selectors';
import { useHasItemExceedingStock } from '../CartDrawer/useHasItemExceedingStock';
import { useShowShoppingBagLoadingIndicator } from '../CartDrawer/useShowShoppingBagLoadingIndicator';
import { Component as PayPalLoader } from '../PayPalLoader';
import ToastContext from '../ToastContext/ToastContext';

const paypalGateway = config.get('public.paypal');

const billingAgreementMessage = defineMessages({
  description: {
    id: 'global_checkout.paypal_billing_agreement_message',
    defaultMessage: 'Thank you for purchasing from SavageX',
  },
});

const PaypalWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing.tiny}px;

  ${({ shouldDisableButton }) =>
    shouldDisableButton &&
    `
    filter: grayscale(100%);
    cursor: not-allowed;

    > * {
      pointer-events: none;
    }
  `}
`;

const getCart = state => state.checkout;

// Returns the cart shipping info from the Redux store, and load
// the shipping options if necessary.
const useCart = () => {
  // Load necessary customer details used by `getShippingOptions`
  useCustomerDetails(['shipping_city', 'shipping_state', 'shipping_zip']);
  const dispatch = useDispatch();
  const cart = useSelector(getCart);
  const { cartId, shippingOptionsNetworkStatus } = cart;
  const shippingInfo = useSelector(getShippingInfo);

  useEffect(() => {
    if (cartId && !shippingOptionsNetworkStatus.isUpToDate) {
      dispatch(getShippingOptions());
    }
  }, [cartId, dispatch, shippingInfo, shippingOptionsNetworkStatus.isUpToDate]);

  return cart;
};

const useAccountAddresses = () => {
  const addresses = useSelector(state => state.account.addresses);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!addresses.networkStatus.isUpToDate) {
      dispatch(loadAddresses());
    }
  }, [addresses.networkStatus, dispatch]);

  return addresses;
};

function PaypalCheckout({
  fromCart = false,
  onSuccess,
  shippingInfo,
  updateLoading: updateLoadingFromProps,
}) {
  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = useDispatch();
  const isPayPalReady = useContext(PayPalLoader.Context);
  const storeGroupId = useSelector(state => state.storeGroup.storeGroupId);
  const { id: customerId } = useCustomer();
  const { tld } = useDomain();
  const {
    shippingOptionId,
    shippingOptions,
    shippingOptionsNetworkStatus,
    cartId,
    isPickupLocation,
  } = useCart();
  const { data: addresses, networkStatus: addressesNetworkStatus } =
    useAccountAddresses();
  const countryList = useSelector(state =>
    getCountryList(state, { addressType: 'shipping' })
  );
  const { showMessage: showToastMessage } = useContext(ToastContext.Context);

  const shouldDisableButton = useHasItemExceedingStock();

  const updateLoading = useCallback(
    value => {
      if (updateLoadingFromProps) {
        updateLoadingFromProps(value);
      }
    },
    [updateLoadingFromProps]
  );

  const isDataLoaded = useMemo(() => {
    return (
      !!cartId &&
      shippingOptionsNetworkStatus.isUpToDate &&
      addressesNetworkStatus.isUpToDate
    );
  }, [cartId, shippingOptionsNetworkStatus, addressesNetworkStatus]);

  const loadPayPalButton = useCallback(() => {
    const MERCHANT_ID = paypalGateway.merchantAccountIdMap[storeGroupId];
    const braintree = global.braintree;
    const paypal = global.paypal;
    const locale = paypalGateway.locale[tld];
    const CLIENT_AUTHORIZATION =
      paypalGateway.key[storeGroupId] || paypalGateway.key;

    // Reference: https://developer.paypal.com/braintree/docs/guides/paypal/vault
    // Create a client.
    braintree.client.create(
      {
        authorization: CLIENT_AUTHORIZATION,
      },
      function (clientErr, clientInstance) {
        // Stop if there was a problem creating the client.
        // This could happen if there is a network error or if the authorization
        // is invalid.
        if (clientErr) {
          Sentry.captureException(clientErr);
          return;
        }

        braintree.dataCollector.create(
          {
            client: clientInstance,
            paypal: true,
          },
          function (err, dataCollectorInstance) {
            const deviceData = dataCollectorInstance?.deviceData || '';
            dispatch(setBraintreeDeviceData(deviceData));
            if (err) {
              Sentry.captureException(err);
            }
          }
        );

        // Create a PayPal Checkout component.
        braintree.paypalCheckout.create(
          {
            client: clientInstance,
            merchantAccountId: MERCHANT_ID,
          },
          function (paypalCheckoutErr, paypalCheckoutInstance) {
            // Stop if there was a problem creating PayPal Checkout.
            // This could happen if there was a network error or if it's incorrectly
            // configured.
            if (paypalCheckoutErr) {
              Sentry.captureException(paypalCheckoutErr);
              return;
            }

            // Set up PayPal with the checkout.js library
            const paypalButtonContainer =
              document.getElementById('paypal-button');
            const paypalButtonCount = paypalButtonContainer.children.length;

            if (paypalButtonCount) {
              const paypalButtonChildren = Array.from(
                paypalButtonContainer.children
              );

              paypalButtonChildren.forEach(button => {
                button.remove();
              });
            }

            paypal.Button.render(
              {
                env: paypalGateway.environment,
                style: {
                  height: 44,
                  size: 'responsive',
                  tagline: false,
                  shape: 'rect',
                  label: 'paypal',
                },
                funding: {
                  disallowed: [paypal.FUNDING.VENMO],
                },
                payment: function () {
                  dispatch(
                    trackExpressCheckoutSelection(
                      'paypal',
                      fromCart ? 'Shopping Bag' : 'Checkout'
                    )
                  );
                  return paypalCheckoutInstance.createPayment({
                    flow: 'vault',
                    billingAgreementDescription: formatMessage(
                      billingAgreementMessage.description
                    ),
                    enableShippingAddress: true,
                    shippingAddressEditable: true,
                  });
                },

                onAuthorize: function (data, actions) {
                  return paypalCheckoutInstance.tokenizePayment(
                    data,
                    async function (err, payload) {
                      if (err || !payload) {
                        Sentry.captureException(err);
                      }

                      const { nonce, details } = payload;
                      let { address } = shippingInfo || {};

                      const defaultAddress = addresses?.find(
                        address => address?.isDefault
                      );

                      /**
                       * Address won't be available if customer is using the paypal address button from /cart
                       * To avoid using a paypal address as the shipping address, we look for the customer's default address
                       * If the customer doesn't have a default address, we use the paypal address
                       */
                      address = address || defaultAddress;

                      // checks if the the storefront ships to the paypal address
                      const canUsePaypalAddressAsShippingAddress =
                        countryList.includes(
                          details.shippingAddress.countryCode
                        );

                      if (!address && !canUsePaypalAddressAsShippingAddress) {
                        updateLoading(false);
                        showToastMessage(
                          <FormattedMessage
                            id="global_checkout.paypal_address_error"
                            defaultMessage="Oops. This address is not available for shipping. Please try again."
                          />
                        );
                        return;
                      }

                      const params = {
                        customerId: customerId,
                        payerId: details.payerId,
                        paymentMethodNonce: nonce,
                        shippingAddress: details.shippingAddress, // this needs to be the paypal address otherwise /authorize will fail
                        onlyUpdateBillingAddress: !!address, // if there is an address or a default address, we only update the billing address
                        ...(address && {
                          previousShippingAddressId: address.id,
                        }),
                      };

                      updateLoading(true);
                      const result = await dispatch(
                        braintreePaypalCheckout(params)
                      );

                      if (result.error || !result.payload) {
                        updateLoading(false);
                        Sentry.captureException(result.error);
                      } else {
                        const { shippingAddress: paypalAddress } =
                          result.payload;

                        const shouldMakeDefaultAddress =
                          (canUsePaypalAddressAsShippingAddress && !address) ||
                          (!address.isDefault &&
                            !isPickupLocation &&
                            canUsePaypalAddressAsShippingAddress);

                        // we only set the paypal address as default if the customer has no previous address
                        if (shouldMakeDefaultAddress) {
                          await dispatch(setDefaultAddress(paypalAddress));
                        }

                        // Covering any edge cases where a shippingOptionId was already defined and the address was updated
                        const isCurrentShippingOptionValid =
                          shippingOptionId &&
                          shippingOptions
                            .find(
                              option =>
                                option.shippingOptionId === shippingOptionId &&
                                !option.excludedCountries.includes(
                                  address
                                    ? address.countryCode
                                    : paypalAddress.countryCode
                                )
                            )
                            .shippingCountries.includes(
                              address // if we have an address we use it, otherwise we use the paypal address
                                ? address.countryCode
                                : paypalAddress.countryCode
                            );

                        const filteredShippingOptions =
                          filterShippingOptionsByAddress(
                            shippingOptions,
                            address || paypalAddress // if we have an address we use it, otherwise we use the paypal address
                          ).filter(
                            // excluding Mondial Relay as there's no scenario where we want chose it ourselves
                            // only the customer can manually pick it
                            option => option.carrierName !== 'Mondial Relay'
                          );

                        await dispatch(
                          updateCartWithShipping(
                            isCurrentShippingOptionValid
                              ? shippingOptionId
                              : filteredShippingOptions[0].shippingOptionId
                          )
                        );

                        onSuccess && (await onSuccess());
                        // if it's in cart, keep loading until the page it redirect to next step
                        !fromCart && updateLoading(false);
                      }
                    }
                  );
                },

                onCancel: function (data) {
                  Sentry.captureMessage(
                    `braintree-client.js payment cancelled ${JSON.stringify(
                      data,
                      0,
                      2
                    )}`,
                    'warning'
                  );
                },

                onError: function (err) {
                  Sentry.captureException(err);
                },
                locale,
              },
              '#paypal-button'
            ).then(function () {
              // The PayPal button will be rendered in an html element with the id
              // `paypal-button`. This function will be called when the PayPal button
              // is set up and ready to be used.
            });
          }
        );
      }
    );
  }, [
    addresses,
    countryList,
    customerId,
    dispatch,
    formatMessage,
    fromCart,
    isPickupLocation,
    onSuccess,
    shippingInfo,
    shippingOptionId,
    shippingOptions,
    showToastMessage,
    storeGroupId,
    tld,
    updateLoading,
  ]);

  useEffect(() => {
    if (!isPayPalReady || !customerId || !isDataLoaded) {
      return;
    }

    // Does not render if a button already exists
    const doesPaypalButtonExists =
      document.getElementsByClassName('paypal-button');
    if (!doesPaypalButtonExists.length) {
      loadPayPalButton();
    }
  }, [customerId, isDataLoaded, isPayPalReady, loadPayPalButton]);

  const { showLoadingIndicator } = useShowShoppingBagLoadingIndicator();
  const shoppingBagFlagStatus = useLDNewShoppingBagUX();
  const { context } = useTheme();

  return (
    <PaypalWrapper
      id="paypal-button"
      shouldDisableButton={
        shoppingBagFlagStatus?.isVariant && context?.cartDrawer
          ? // we need to manually disable the paypal button when showing a loading indicator on the whole shopping bag
            showLoadingIndicator || shouldDisableButton
          : shouldDisableButton
      }
    />
  );
}

PaypalCheckout.propTypes = {
  fromCart: PropTypes.bool,
  onSuccess: PropTypes.func,
  updateLoading: PropTypes.func,
  shippingInfo: PropTypes.object,
};

PaypalCheckout.PaypalWrapper = PaypalWrapper;

export default PaypalCheckout;
